/**
 * Normalize broker numeric values for display and UI state (especially Connect Trade).
 * Quantities: integers when whole; prices/money: 2 decimal places.
 */

function parseBrokerNumber(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  const trimmed = String(value).trim();
  if (!trimmed) return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : null;
}

function isEffectivelyInteger(n) {
  return Math.abs(n - Math.round(n)) < 1e-8;
}

export function formatBrokerQuantity(value) {
  if (value == null || value === '') return value;
  const n = parseBrokerNumber(value);
  if (n == null) return value;
  if (isEffectivelyInteger(n)) return String(Math.round(n));
  return String(parseFloat(n.toFixed(4)));
}

/** Positive qty only — treats 0 / "0" / "0.000000" as missing. */
export function positiveBrokerQuantity(value) {
  const n = parseBrokerNumber(value);
  if (n == null || n <= 0) return null;
  return formatBrokerQuantity(n);
}

export function formatBrokerPrice(value) {
  if (value == null || value === '') return value;
  const n = parseBrokerNumber(value);
  if (n == null) return value;
  return n.toFixed(2);
}

export function formatBrokerMoney(value) {
  return formatBrokerPrice(value);
}

/** Coerce quantity to a number suitable for order-entry state (integer when whole). */
export function coerceBrokerQuantityNumber(value) {
  const formatted = formatBrokerQuantity(value);
  const n = parseBrokerNumber(formatted);
  return n == null ? null : n;
}

const POSITION_QTY_FIELDS = ['Quantity'];
const POSITION_PRICE_FIELDS = [
  'AveragePrice',
  'Last',
  'MarkToMarketPrice',
  'UnrealizedProfitLoss',
  'TodaysProfitLoss',
  'MarketValue',
  'TotalCost',
];
const ORDER_QTY_FIELDS = ['Quantity', 'FilledQuantity', 'RemainingQuantity'];
const ORDER_PRICE_FIELDS = ['LimitPrice', 'StopPrice', 'FilledPrice'];
const LEG_QTY_FIELDS = ['QuantityOrdered', 'ExecQuantity', 'Quantity'];
const LEG_PRICE_FIELDS = ['ExecutionPrice', 'ExecPrice'];

function applyFieldFormats(obj, qtyFields, priceFields) {
  if (!obj || typeof obj !== 'object') return obj;
  const out = { ...obj };
  for (const key of qtyFields) {
    if (out[key] != null && out[key] !== '') {
      out[key] = formatBrokerQuantity(out[key]);
    }
  }
  for (const key of priceFields) {
    if (out[key] != null && out[key] !== '') {
      out[key] = formatBrokerPrice(out[key]);
    }
  }
  return out;
}

export function normalizeBrokerPosition(position) {
  if (!position || typeof position !== 'object') return position;
  return applyFieldFormats(position, POSITION_QTY_FIELDS, POSITION_PRICE_FIELDS);
}

function tradeActionToBuyOrSell(tradeAction) {
  const action = String(tradeAction || '').toUpperCase();
  const map = {
    BUY: 'Buy',
    SELL: 'Sell',
    BUYTOCOVER: 'BuyToCover',
    SELLSHORT: 'SellShort',
    BUYTOOPEN: 'BuyToOpen',
    BUYTOCLOSE: 'BuyToClose',
    SELLTOOPEN: 'SellToOpen',
    SELLTOCLOSE: 'SellToClose',
  };
  return map[action] || tradeAction;
}

export function normalizeBrokerOrder(order) {
  if (!order || typeof order !== 'object') return order;

  let normalized = applyFieldFormats(order, ORDER_QTY_FIELDS, ORDER_PRICE_FIELDS);

  // Connect Trade often sends order_limit_price "0" on MKT/STP; strip so chart drag
  // hit-testing does not prefer LimitPrice over StopPrice.
  const ot = String(normalized.OrderType || normalized.order_type || '');
  const limitN = parseBrokerNumber(normalized.LimitPrice);
  if (
    limitN === 0 &&
    (ot === 'Market' ||
      ot === 'StopMarket' ||
      ot === 'MKT' ||
      ot === 'STP')
  ) {
    normalized = { ...normalized, LimitPrice: undefined };
  }

  const qty =
    normalized.Quantity ??
    formatBrokerQuantity(normalized.order_qty);
  const filled =
    normalized.FilledQuantity ??
    formatBrokerQuantity(normalized.filled_qty);

  if (qty != null) normalized = { ...normalized, Quantity: formatBrokerQuantity(qty) };
  if (filled != null) normalized = { ...normalized, FilledQuantity: formatBrokerQuantity(filled) };
  if (normalized.RemainingQuantity != null) {
    normalized.RemainingQuantity = formatBrokerQuantity(normalized.RemainingQuantity);
  }

  if (Array.isArray(normalized.Legs) && normalized.Legs.length > 0) {
    normalized.Legs = normalized.Legs.map((leg) =>
      applyFieldFormats(leg, LEG_QTY_FIELDS, LEG_PRICE_FIELDS)
    );
  } else {
    const symbol = normalized.Symbol ?? normalized.symbol;
    const side = normalized.TradeAction ?? normalized.side;
    if (symbol || qty != null) {
      normalized.Legs = [
        {
          Symbol: symbol,
          BuyOrSell: tradeActionToBuyOrSell(side),
          QuantityOrdered: formatBrokerQuantity(qty),
          ExecQuantity: formatBrokerQuantity(filled),
        },
      ];
    }
  }

  return normalized;
}

export function normalizeBrokerOrders(orders) {
  if (!Array.isArray(orders)) return orders;
  return orders.map(normalizeBrokerOrder);
}
